import React from "react";
import styled from "styled-components";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { formatCurrency } from "../config/market";
import { minimumOrderMessage, minimumOrderExplainer } from "../utils/minimumOrder";

/**
 * MinimumOrderNotice: the ONE way every cart surface tells the customer their
 * basket is too small to be delivered, while they are still in the cart and a
 * tap away from fixing it.
 *
 * Reads a shortfall computed by utils/minimumOrder (the server remains the real
 * gate). Renders nothing when there is nothing missing, so a cart can drop it
 * in unconditionally beside CoverageBanner.
 *
 * Presentation-only and design-token light, like CoverageBanner, so the message
 * reads the same in all five carts. It asks rather than scolds: the ask first,
 * the reason after it, and no second sentence beyond that.
 */
const MinimumOrderNotice = ({
  shortfall,
  minimum,
  vendorName,
  format = formatCurrency,
  /* The hub stacks one of these per vendor group, where the surprising part is
     not the number but that each vendor needs its own. */
  perVendor = false,
  variant = "inline", // 'inline' | 'block'
  className,
  style,
}) => {
  const gap = Number(shortfall);
  if (!(gap > 0)) return null;

  return (
    <Notice $variant={variant} className={className} style={style} role="status">
      <AddShoppingCartIcon fontSize="small" />
      <Text>
        {vendorName ? <b>{vendorName}: </b> : null}
        {minimumOrderMessage(gap, format)}. {minimumOrderExplainer(minimum, format)}
        {perVendor ? " Each vendor is delivered separately, so each one has to reach it on its own." : ""}
      </Text>
    </Notice>
  );
};

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: ${(p) => (p.$variant === "block" ? "14px 16px" : "10px 12px")};
  border-radius: 10px;
  margin: ${(p) => (p.$variant === "block" ? "12px 0" : "6px 0")};
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  border: 1px solid #f0d9a8;
  background: #fdf7e8;
  color: #7a4e07;

  b {
    font-weight: 700;
  }

  svg {
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const Text = styled.span`
  flex: 1;
`;

export default MinimumOrderNotice;
